import axios from 'axios';

// Recipe sources
const THEMEALDB_API = 'https://www.themealdb.com/api/json/v1/1';
const SPOONACULAR_API = 'https://api.spoonacular.com/recipes';

// API key for Spoonacular
const SPOONACULAR_KEY = "ee41ce2c810d428996bfca608e52d919";

// Get user's location
const getUserLocation = async () => {
  try {
    const response = await axios.get('https://ipapi.co/json/');
    return {
      country: response.data.country_name,
      region: response.data.region,
      city: response.data.city
    };
  } catch (error) {
    console.error('Error getting location:', error);
    return null;
  }
};

// Get cuisine based on location
const getCuisineByLocation = (country) => {
  const cuisineMap = {
    'India': 'Indian',
    'China': 'Chinese',
    'Japan': 'Japanese',
    'Italy': 'Italian',
    'Mexico': 'Mexican',
    'Thailand': 'Thai',
    // Add more mappings as needed
  };
  return cuisineMap[country] || 'American'; // Default to American if not found
};

// Combine and normalize recipe data from different sources
const normalizeRecipe = (recipe, source) => {
  switch (source) {
    case 'themealdb':
      return {
        id: recipe.idMeal,
        title: recipe.strMeal,
        image: recipe.strMealThumb,
        cuisine: recipe.strArea || 'Unknown',
        category: recipe.strCategory,
        instructions: recipe.strInstructions,
        ingredients: Object.keys(recipe)
          .filter(key => key.startsWith('strIngredient') && recipe[key])
          .map(key => ({
            name: recipe[key],
            measure: recipe[`strMeasure${key.slice(13)}`]
          })),
        source: 'TheMealDB'
      };

    case 'spoonacular':
      if (!recipe.extendedIngredients) {
        // Return basic info for recipe cards
        return {
          id: recipe.id,
          title: recipe.title,
          image: recipe.image,
          cuisine: recipe.cuisines?.[0] || 'Unknown',
          category: recipe.dishTypes?.[0],
          source: 'Spoonacular',
          needsDetails: true // Flag to indicate we need to fetch details
        };
      }
      // Return full info for recipe modal
      return {
        id: recipe.id,
        title: recipe.title,
        image: recipe.image,
        cuisine: recipe.cuisines?.[0] || 'Unknown',
        category: recipe.dishTypes?.[0],
        instructions: recipe.instructions,
        ingredients: recipe.extendedIngredients.map(ing => ({
          name: ing.name,
          measure: `${ing.amount} ${ing.unit}`
        })),
        source: 'Spoonacular',
        needsDetails: false
      };

    default:
      return recipe;
  }
};

// Get recipes by cuisine
const getRecipesByCuisine = async (cuisine) => {
  try {
    // Get TheMealDB recipes
    let mealdbRecipes = [];
    try {
      const mealdbFilterRes = await axios.get(`${THEMEALDB_API}/filter.php?a=${cuisine}`);
      if (mealdbFilterRes.data.meals) {
        const mealPromises = mealdbFilterRes.data.meals
          .slice(0, 10)
          .map(meal => 
            axios.get(`${THEMEALDB_API}/lookup.php?i=${meal.idMeal}`)
          );
        
        const mealResponses = await Promise.all(mealPromises);
        mealdbRecipes = mealResponses
          .map(response => response.data.meals?.[0])
          .filter(Boolean)
          .map(recipe => normalizeRecipe(recipe, 'themealdb'));
      }
    } catch (error) {
      console.error('Error fetching from TheMealDB:', error);
    }

    // Get Spoonacular recipes
    let spoonacularRecipes = [];
    try {
      const spoonacularRes = await axios.get(
        `${SPOONACULAR_API}/complexSearch`, {
          params: {
            cuisine: cuisine,
            apiKey: SPOONACULAR_KEY,
            addRecipeInformation: true,
            number: 10,
            instructionsRequired: true
          }
        }
      );
      
      if (spoonacularRes.data.results) {
        spoonacularRecipes = spoonacularRes.data.results
          .filter(recipe => recipe.title && recipe.image)
          .map(recipe => normalizeRecipe(recipe, 'spoonacular'));
      }
    } catch (error) {
      console.error('Error fetching from Spoonacular:', error);
    }

    // Combine recipes from both sources
    const recipes = [...mealdbRecipes, ...spoonacularRecipes];
    
    return recipes.length > 0 ? recipes : [];

  } catch (error) {
    console.error('Error fetching recipes:', error);
    return [];
  }
};

// Search recipes across all sources
const searchRecipes = async (query) => {
  try {
    // Search TheMealDB
    let mealdbRecipes = [];
    try {
      const mealdbRes = await axios.get(`${THEMEALDB_API}/search.php?s=${query}`);
      if (mealdbRes.data.meals) {
        mealdbRecipes = mealdbRes.data.meals
          .map(recipe => normalizeRecipe(recipe, 'themealdb'));
      }
    } catch (error) {
      console.error('Error searching TheMealDB:', error);
    }

    // Search Spoonacular
    let spoonacularRecipes = [];
    try {
      const spoonacularRes = await axios.get(
        `${SPOONACULAR_API}/complexSearch`, {
          params: {
            query: query,
            apiKey: SPOONACULAR_KEY,
            addRecipeInformation: true,
            number: 10,
            instructionsRequired: true
          }
        }
      );
      
      if (spoonacularRes.data.results) {
        spoonacularRecipes = spoonacularRes.data.results
          .filter(recipe => recipe.title && recipe.image)
          .map(recipe => normalizeRecipe(recipe, 'spoonacular'));
      }
    } catch (error) {
      console.error('Error searching Spoonacular:', error);
    }

    // Combine recipes from both sources
    const recipes = [...mealdbRecipes, ...spoonacularRecipes];
    
    return recipes.length > 0 ? recipes : [];

  } catch (error) {
    console.error('Error searching recipes:', error);
    return [];
  }
};

// Add this new function to fetch detailed recipe info
const getSpoonacularRecipeDetails = async (id) => {
  try {
    const response = await axios.get(
      `${SPOONACULAR_API}/${id}/information`, {
        params: {
          apiKey: SPOONACULAR_KEY
        }
      }
    );
    
    return normalizeRecipe(response.data, 'spoonacular');
  } catch (error) {
    console.error('Error fetching recipe details:', error);
    throw error;
  }
};

export {
  getUserLocation,
  getCuisineByLocation,
  getRecipesByCuisine,
  searchRecipes,
  getSpoonacularRecipeDetails
}; 